import React, { useMemo } from 'react';
import type { ReactNode } from 'react';
import { getSafeCountryFromCode, CountryProvider } from '@tectonics/commons/country';
import { getTenantFromCode, TenantProvider } from '@tectonics/commons/tenant';
import { getQueryParamValue } from '@tectonics/utils/url';
import { VolcanesThemeProvider, getThemeById } from '@tectonics/look-and-feel/volcanes/theme';
import { GlobalStyle } from '@tectonics/look-and-feel/volcanes/commons/global-style';
import { PaymentProvider, usePayment } from './context';
import { usePaymentState } from './hooks/use-payment-state';
import { LoadingScreen } from './screens/loading-screen';
import { SuccessScreen } from './screens/success-screen';
import { ErrorScreen } from './screens/error-screen';
import { PageContainer } from './controller.styles';

interface Props {
  readonly tenantCode: string | null | undefined;
}

export function PixJsrPaymentController({ tenantCode }: Props): ReactNode {
  if (!tenantCode) {
    throw new Error('URL query param "tenant" not found');
  }

  const hash = getQueryParamValue('hash');
  if (!hash) {
    throw new Error('URL query param "hash" not found');
  }

  const tenant = getTenantFromCode(tenantCode);
  const country = getSafeCountryFromCode('br');
  const theme = getThemeById('default');

  return (
    <TenantProvider value={tenant}>
      <CountryProvider value={country}>
        <VolcanesThemeProvider theme={theme}>
          <>
            <GlobalStyle />
            <PaymentRoot paymentHash={hash} />
          </>
        </VolcanesThemeProvider>
      </CountryProvider>
    </TenantProvider>
  );
}

interface PaymentRootProps {
  readonly paymentHash: string;
}

function PaymentRoot({ paymentHash }: PaymentRootProps): ReactNode {
  const [data, dispatch] = usePaymentState(paymentHash);

  const contextValue = useMemo(() => ({ data, dispatch }), [data, dispatch]);

  return (
    <PaymentProvider value={contextValue}>
      <PageContainer>
        <PaymentScreen />
      </PageContainer>
    </PaymentProvider>
  );
}

function PaymentScreen(): ReactNode {
  const { data } = usePayment();

  switch (data.state) {
    case 'LOADING':
    case 'AUTHENTICATING':
    case 'SUBMITTING_AUTH':
      return <LoadingScreen />;
    case 'SUCCESS':
      return <SuccessScreen />;
    case 'ERROR':
      return <ErrorScreen />;
    default:
      return <LoadingScreen />;
  }
}
